// Diffs a pending Scene against a live Scene so the UI can annotate each item
// as New / Modified / Removed. Runs on demand — the two scenes are read from
// useFloorDesignSync's returned state.
//
// "Item" here means any addressable entity in the Scene the operator can
// re-place: items (furniture), walls, custom rooms, custom clusters, and
// teams. We only compare identity + geometry-ish fields; text tweaks that
// don't move a component still count as Modified for a truthful count.

import type { Scene } from "./types";

export type ChangeKind = "new" | "modified" | "removed";
export type ItemKind =
  | "item"
  | "wall"
  | "customRoom"
  | "customCluster"
  | "team";

export interface SceneChange {
  kind: ItemKind;
  id: string;
  change: ChangeKind;
  label?: string;
}

export interface SceneDiff {
  changes: SceneChange[];
  totals: Record<ChangeKind, number>;
  isDirty: boolean;
}

// A very cheap stable hash so we can compare "shape" without dragging
// deep-equal in. Sorts keys implicitly by JSON.stringify walking the object
// in insertion order — good enough since our scene mutations always spread
// old → new (same key order).
function shapeOf(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function toMap<T extends { id?: string }>(arr: T[] | undefined): Map<string, T> {
  const m = new Map<string, T>();
  if (!arr) return m;
  for (const x of arr) {
    if (x && typeof (x as { id?: unknown }).id === "string") {
      m.set((x as { id: string }).id, x);
    }
  }
  return m;
}

function diffMap<T>(
  kind: ItemKind,
  liveMap: Map<string, T>,
  draftMap: Map<string, T>,
  labelOf: (v: T) => string | undefined,
): SceneChange[] {
  const out: SceneChange[] = [];
  for (const [id, v] of draftMap) {
    const prior = liveMap.get(id);
    if (prior == null) {
      out.push({ kind, id, change: "new", label: labelOf(v) });
    } else if (shapeOf(prior) !== shapeOf(v)) {
      out.push({ kind, id, change: "modified", label: labelOf(v) });
    }
  }
  for (const [id, v] of liveMap) {
    if (!draftMap.has(id)) {
      out.push({ kind, id, change: "removed", label: labelOf(v) });
    }
  }
  return out;
}

export function diffScenes(
  live: Scene | null | undefined,
  draft: Scene | null | undefined,
): SceneDiff {
  const empty: SceneDiff = {
    changes: [],
    totals: { new: 0, modified: 0, removed: 0 },
    isDirty: false,
  };
  if (!live || !draft) return empty;

  const label = (v: unknown): string | undefined => {
    const o = v as { label?: unknown; name?: unknown };
    if (typeof o?.label === "string") return o.label;
    if (typeof o?.name === "string") return o.name;
    return undefined;
  };

  const changes: SceneChange[] = [
    ...diffMap("item", toMap(live.items), toMap(draft.items), label),
    ...diffMap("wall", toMap(live.walls), toMap(draft.walls), label),
    ...diffMap("customRoom", toMap(live.customRooms), toMap(draft.customRooms), label),
    ...diffMap("team", toMap(live.teams), toMap(draft.teams), label),
    // customClusters is keyed as a record on Scene — turn into an array first.
    ...diffMap(
      "customCluster",
      new Map(Object.entries(live.customClusters ?? {}).map(([k, v]) => [k, v as unknown])),
      new Map(Object.entries(draft.customClusters ?? {}).map(([k, v]) => [k, v as unknown])),
      label,
    ),
  ];

  const totals = { new: 0, modified: 0, removed: 0 };
  for (const c of changes) totals[c.change]++;
  return { changes, totals, isDirty: changes.length > 0 };
}
